import React, { useState, useMemo } from "react";
import { Modal, Checkbox, Button, notification } from "antd";
import axiosInstance from "../../../core/index-core";
import { metrics } from "../../../metrics/client";
import dayjs from "dayjs";

const AttestationModal = ({
  visible,
  onCancel,
  onSuccess,
  patient_id,
  session_id,
  noteJSON,
  formUpdates,
  clinicianName,
  sessionDate,
  setNoteSubmitted,
  onNoteLocked
}) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [uploading, setUploading] = useState(false);

  const clinicianNameSplitted = useMemo(() => {
    if (!clinicianName) return "";
    return clinicianName
      .split(",")
      .map((part) =>
        part
          .trim()
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase())
      )
      .join(", ");

  }, [clinicianName])

  // Map backend error codes (and HTTP status) to clinician-facing messages.
  // Keep message short; let description carry the actionable detail.
  const getServerErrorMessage = (err) => {
    const code = err?.response?.data?.error_code;
    const reason = err?.response?.data?.reason || err?.response?.data?.detail;
    const status = err?.response?.status;

    switch (code) {
      case "GENERATION_OVERWRITE_GUARD":
        // Should be rare after backend silent-no-op; kept for safety.
        return {
          message: "Your note was auto-saved during AI generation.",
          description:
            "The AI-generated content is preserved. Please refresh to see the latest version.",
        };
      case "NOTE_LOCKED_SIGNED":
        return {
          message: "This note is already signed in AMD.",
          description:
            "Signed notes are part of the permanent medical record and cannot be edited from AIspire. If changes are needed, amend it in AMD.",
        };
      case "INVALID_FIELD_VALUES":
        return {
          message: "Note contains values that AMD does not accept.",
          description: reason || "Review the highlighted fields and try again.",
        };
      case "EHR_LOCKED":
      case "VERSION_CONFLICT":
        return {
          message: "This note has been updated by another action.",
          description:
            "Please refresh the page to load the latest version, then try again.",
        };
      default:
        if (status === 429) {
          return {
            message: "AMD is temporarily throttling our requests.",
            description: "Please try again in 30 seconds.",
          };
        }
        if (reason && /structure unavailable from AMD/i.test(reason)) {
          return {
            message: "Could not retrieve note structure from AMD.",
            description:
              "AMD may be throttling our requests; please try again in 30 seconds.",
          };
        }
        if (status >= 500 && status < 600) {
          return {
            message: "Connection issue saving the note.",
            description:
              reason || "Please try again. If this continues, contact support.",
          };
        }
        return {
          message: "Unable to save note.",
          description: reason || "Please try again.",
        };
    }
  };

  const saveNote = async () => {
    if (!noteJSON?.note_id) {
      notification.error({ message: "Note ID missing, cannot save." });
      return;
    }
    setUploading(true);
    try {
      await axiosInstance.patch(
        `patient/${patient_id}/sessions/${session_id}/notes/${noteJSON?.note_id}`,
        { ...formUpdates }
      );
      notification.success({ message: "Note Saved Successfully." });
      setConsentChecked(false);
      setNoteSubmitted(true);
      // Notify parent that note has been locked after successful submission
      onNoteLocked?.();
      onSuccess?.();
    } catch (err) {
      const { message, description } = getServerErrorMessage(err);
      notification.error({ message, description, duration: 6 });
      // Telemetry: EHR submit failure
      metrics.add(metrics.ehrSubmitErrorEvent({
        session_id,
        patient_id,
        note_id: noteJSON?.note_id,
        error_code: err?.response?.data?.error_code || null,
        http_status: err?.response?.status || null,
        error_message:
          err?.response?.data?.detail ||
          err?.response?.data?.reason ||
          err?.message ||
          "Unable to save note",
      }));
      metrics.flush(); // Flush immediately — high-value domain error event
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Clinician Attestation"
      centered
      onCancel={() => {
        setConsentChecked(false);
        onCancel?.();
      }}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!consentChecked}
          loading={uploading}
          onClick={saveNote}
        >
          Submit
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        I{clinicianName ? "," : ""}&nbsp;
        {clinicianNameSplitted} attest that the clinical documentation for the patient
        encounter dated&nbsp;
        {dayjs(sessionDate).format("DD-MM-YYYY H:mm")}, accurately reflects my professional medical judgement,
        observations, and actions during the visit.
      </div>
      <Checkbox
        checked={consentChecked}
        onChange={(e) => setConsentChecked(e.target.checked)}
      >
        I agree and attest to the above statement.
      </Checkbox>
    </Modal>
  );
};

export default AttestationModal;
